import { Check, PartyPopper, Calendar, MapPin, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { eventInfo } from '../data/landingData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistrationSuccess() {
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await fetch(`${API}/checkout/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentData(data);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] noise-texture py-20 px-6">
      <div className="max-w-2xl mx-auto text-center">
        {/* Success icon */}
        <div className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-8">
          <Check className="w-12 h-12 text-emerald-400" />
        </div>

        {/* Main heading */}
        <h1 className="font-cormorant text-4xl md:text-5xl font-medium text-white mb-4">
          Welcome to The Brotherhood, King!
        </h1>

        <p className="text-zinc-400 font-manrope text-lg mb-8">
          Your registration for The Reset has been confirmed. Get ready for a transformative experience.
        </p>

        {/* Payment details */}
        {paymentData && (
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-8 text-left">
            <h3 className="font-manrope font-semibold text-white mb-4">Payment Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-500 font-manrope">Amount Paid</span>
                <span className="text-white font-manrope font-medium">
                  ${((paymentData.amount_total || 0) / 100).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500 font-manrope">Status</span>
                <span className="text-emerald-400 font-manrope font-medium capitalize">
                  {paymentData.payment_status}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Event details */}
        <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <PartyPopper className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-manrope uppercase tracking-wider text-sm font-bold">
              Mark Your Calendar
            </span>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Calendar className="w-5 h-5 text-zinc-400" />
              <span className="text-white font-manrope">{eventInfo.dates}</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <MapPin className="w-5 h-5 text-zinc-400" />
              <span className="text-white font-manrope">{eventInfo.venue}, {eventInfo.location}</span>
            </div>
          </div>
        </div>

        {/* Next steps */}
        <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-8 text-left">
          <h3 className="font-manrope font-semibold text-white mb-4">What's Next?</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300 font-manrope">Check your email for confirmation and receipt</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300 font-manrope">You'll receive a detailed packing list and itinerary closer to the event</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300 font-manrope">Book your flights to Tocumen International Airport (PTY)</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <span className="text-zinc-300 font-manrope">Airport shuttle included - arrive before 9:00 PM on Thursday</span>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div className="flex items-center justify-center gap-2 text-zinc-500 font-manrope">
          <Mail className="w-4 h-4" />
          <span>Questions? Contact us at </span>
          <a href="mailto:askcoachneil@gmail.com" className="text-[#D4AF37] hover:underline">
            askcoachneil@gmail.com
          </a>
        </div>

        {/* Back button */}
        <a
          href="/"
          className="inline-block mt-8 text-zinc-400 hover:text-white font-manrope transition-colors"
        >
          ← Back to homepage
        </a>
      </div>
    </div>
  );
}
